import React from "react";
import { observer } from "mobx-react";
import styles from "./OrderStatusList.module.scss";
import { columns } from "./columns";
import { saveAgGridColumnSize } from "modules/common/services/table/size-storage";
import { DefaultAgGridOptions, rowIdBy } from "modules/common/services/table/helpers";
import { RemoveConfirmation } from "modules/common/components/form/RemoveConfirmation";
import { agGridEntityContextMenu } from "modules/common/components/table/EntityContextMenu";
import { OrderStatusListType, OrderStatusList } from "../../models/order-status-list";
import { OrderStatusSnapshotType, formatOrderStatus } from "../../models/order-status";
import copyToClipboard from "copy-to-clipboard";

import {
  GridOptions,
  GetContextMenuItemsParams,
  CellContextMenuEvent,
  GridReadyEvent,
  RowDoubleClickedEvent,
} from "ag-grid-community";
import { GridTheme } from "modules/common/components/table/GridTheme";
import { AgGridReact } from "ag-grid-react";
import { agGridLocale } from "modules/root/services/locale";
import { MAX_INT } from "modules/common/constants";
import { PlanrButton } from "modules/common/components/planr/button/Button";

const TABLE_NAME = OrderStatusList.name;
const sizeSaver = saveAgGridColumnSize(TABLE_NAME);
const config = columns(TABLE_NAME);

export const OrderStatusesList = observer(
  class extends React.Component<OrderStatusesListProps> {
    private options: GridOptions = DefaultAgGridOptions(true);

    render() {
      const { store, onSelected } = this.props;

      return (
        <RemoveConfirmation<OrderStatusSnapshotType>
          observation={store.data}
          what={this.removeLabel}
          onConfirmed={this.removeStatus}
          render={({ confirmRemoving }) => {
            const getContextMenuItems = ({ node }: GetContextMenuItemsParams) => {
              // меню вызвано не на строке — показывать нечего
              if (!node) {
                return [];
              }

              return agGridEntityContextMenu({
                onCopy: () => copyToClipboard(formatOrderStatus(node.data), { format: "text/plain" }),
                onChange: () => onSelected(getRowNodeId(node.data)),
                onRemove: () => confirmRemoving(node.data),
              });
            };

            return (
              <>
                <GridTheme bordered={true} className={`designed ${styles.grid}`}>
                  <AgGridReact
                    rowStyle={{
                      borderBottom: "1 px solid #EDF0F2",
                      fontFamily: "GothamPro, Arial, sans-serif",
                      color: "#00273D",
                      height: "32px",
                      fontSize: "12px",
                    }}
                    rowSelection="single"
                    suppressCellFocus={true}
                    enableCellTextSelection={true}
                    columnDefs={config}
                    rowData={store.data}
                    localeText={agGridLocale}
                    onRowDoubleClicked={this.onRowSelected}
                    getRowId={getRowId}
                    getContextMenuItems={getContextMenuItems}
                    onGridReady={this.onGridReady}
                    onCellContextMenu={this.onCellContextMenu}
                    onSortChanged={store.sorter.resorted}
                    onColumnResized={sizeSaver}
                    gridOptions={this.options}
                    rowBuffer={MAX_INT}
                  />
                </GridTheme>
                <div className={styles.cardList}>
                  {store.data.map((row) => {
                    return (
                      <div key={row.id}>
                        <div className={styles.card}>
                          <div className={styles.mid}>
                            <div className={styles.label}>Статус:</div>
                            <div className={styles.row}>{row.name}</div>

                            {row.description && (
                              <>
                                <div className={styles.label}>Описание:</div>
                                <div className={styles.row}>{row.description}</div>
                              </>
                            )}
                            <div className={styles.label}>Цвет:</div>
                            <div className={styles.row}>
                              <span style={{ backgroundColor: row.color, color: row.color, width: "150px" }}>
                                sdadas
                              </span>
                            </div>
                          </div>
                          <div className={styles.edit}>
                            <PlanrButton
                              type="lightblueish"
                              icon="general-edit"
                              size="small"
                              onClick={() => {
                                this.props.onSelected(row.id);
                              }}
                            />
                            <PlanrButton
                              type="neutral"
                              icon={"general-trash"}
                              size="small"
                              style={{ marginLeft: "0px", marginTop: "5px", color: "#e31818" }}
                              onClick={() => {
                                confirmRemoving(row);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          }}
        />
      );
    }

    removeLabel = (status: OrderStatusSnapshotType) => `статус договора ${status.name}`;

    removeStatus = async (status: OrderStatusSnapshotType) => {
      const { store, onRemoved } = this.props;
      const success = await store.delete(status);
      success && onRemoved();
    };

    onCellContextMenu = ({ node }: CellContextMenuEvent) => {
      node && node.setSelected(true, true);
    };

    onRowSelected = ({ data }: RowDoubleClickedEvent) => {
      const { onSelected } = this.props;
      onSelected(getRowNodeId(data));
    };

    onGridReady = async ({ api }: GridReadyEvent) => {
      const { sorter } = this.props.store;
      setTimeout(() => api.applyColumnState(sorter.agGridSortState));
    };
  }
);

const getRowNodeId = (e: OrderStatusSnapshotType) => e.id;
const getRowId = rowIdBy(getRowNodeId);

interface OrderStatusesListProps {
  onRemoved: () => void;
  onSelected: (id: string) => void;
  store: OrderStatusListType;
}
